import { z } from "zod";

import { typeAllergeneSchema } from "./intrants.js";
import { idSchema } from "./parcellaire.js";
import { dateIsoSchema } from "./vendange.js";

/** Décision documentaire : ne complète pas le graphe physique et ne certifie pas l'AOC. */
const decisionDonneesSchema = z
  .object({
    statut: z.enum(["documentee", "a_qualifier"]),
    couleur_resultat: z.enum(["blanc", "rose", "inconnu"]),
    mention_millesime: z.enum(["bsa", "millesime", "a_qualifier"]),
    millesime_resultat: z.number().int().min(1900).max(9999).nullable(),
    composition_documentaire: z.string().trim().min(1).max(5000).nullable(),
    allergenes_statut: z.enum(["documentes", "a_qualifier"]),
    allergenes: z
      .array(typeAllergeneSchema)
      .max(3)
      .refine(
        (valeurs) => new Set(valeurs).size === valeurs.length,
        "Un allergène ne peut figurer qu'une fois.",
      ),
    reference_justificatif: z.string().trim().min(1).max(500).nullable(),
    justification: z.string().trim().min(1).max(5000).nullable(),
    date_decision: dateIsoSchema,
    motif_remplacement: z.string().trim().min(1).max(1000).nullable(),
  })
  .strict();

type DecisionDonnees = z.infer<typeof decisionDonneesSchema>;

function verifierDecision(data: DecisionDonnees, ctx: z.RefinementCtx): void {
  const erreur = (champ: keyof DecisionDonnees, message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [champ], message });
  if ((data.mention_millesime === "millesime") !== (data.millesime_resultat !== null)) {
    erreur("millesime_resultat", "Une année est requise uniquement pour la mention millésimée.");
  }
  if (data.statut !== "documentee") return;
  if (data.couleur_resultat === "inconnu")
    erreur("couleur_resultat", "Documentez la couleur du résultat.");
  if (data.mention_millesime === "a_qualifier")
    erreur("mention_millesime", "Documentez la mention BSA ou millésimée.");
  if (data.composition_documentaire === null)
    erreur("composition_documentaire", "Documentez la composition du résultat.");
  if (data.allergenes_statut !== "documentes")
    erreur("allergenes_statut", "Documentez les allergènes du résultat, y compris leur absence.");
  if (data.reference_justificatif === null)
    erreur("reference_justificatif", "Référencez le dossier justificatif.");
  if (data.justification === null) erreur("justification", "Justifiez la décision documentaire.");
}

export const qualifierResultatDegorgementInput = z
  .object({
    degorgement_id: idSchema,
    /** Zéro pour la première décision ; contrôle optimiste sur la dernière version, retrait inclus. */
    version_attendue: z.number().int().min(0).max(2_147_483_646),
    data: decisionDonneesSchema.superRefine(verifierDecision),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (input.version_attendue > 0 !== (input.data.motif_remplacement !== null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["data", "motif_remplacement"],
        message: "Un motif est requis uniquement pour remplacer une décision existante.",
      });
    }
  });
export type QualifierResultatDegorgementInput = z.infer<typeof qualifierResultatDegorgementInput>;

/** Dernière version, même si elle retire une qualification antérieure. */
export const decisionQualificationDegorgementSchema = decisionDonneesSchema
  .extend({
    id: idSchema,
    domaine_id: idSchema,
    degorgement_id: idSchema,
    version: z.number().int().positive(),
    remplace_decision_id: idSchema.nullable(),
    operateur_membre_id: idSchema,
    created_at: z.string().datetime(),
  })
  .superRefine(verifierDecision);
export type DecisionQualificationDegorgement = z.infer<
  typeof decisionQualificationDegorgementSchema
>;
